import { useState, useEffect, useRef, useCallback } from "react";
import { TextField } from "@mui/material";
import "../../../assets/css/CustomMulitLineTextField.css";

interface CustomTextInputProps {
  label: string;
  value: string | number;
  onChange: (value: string | number) => void;
  fullWidth?: boolean;
  margin?: "none" | "dense" | "normal";
  delay?: number;
}

const CustomMulitLineTextField: React.FC<CustomTextInputProps> = ({
  label,
  value,
  onChange,
  fullWidth = true,
  delay = 1000,
}) => {
  const [localValue, setLocalValue] = useState<string | number>(value);
  // Edit typed but not yet sent to onChange (debounce still pending).
  const pendingRef = useRef<{ value: string | number } | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    // An external value change replaces any pending local edit.
    valueRef.current = value;
    pendingRef.current = null;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setLocalValue(value);
  }, [value]);

  // Sends the pending edit (if any, and if it differs from the current value)
  // immediately instead of waiting for the debounce.
  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending && pending.value !== valueRef.current) {
      onChangeRef.current(pending.value);
    }
  }, []);

  // On unmount (e.g. navigating away or closing an editor), send the pending
  // edit rather than dropping it.
  useEffect(() => flush, [flush]);

  return (
    <TextField
      className="text-box-multiline-edit"
      label={label}
      fullWidth={fullWidth}
      multiline={true}
      minRows={5}
      variant="outlined"
      type="text"
      value={localValue}
      onChange={(e) => {
        const raw = e.target.value;
        setLocalValue(raw);
        if (delay === 0) {
          onChange(raw);
          return;
        }
        pendingRef.current = { value: raw };
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(flush, delay);
      }}
      onBlur={flush}
    />
  );
};

export { CustomMulitLineTextField };
